/**
 * SidebarPhaseConfigTab Component
 *
 * Tab for configuring sidebar phases and page organization.
 * Features accordion for each phase with drag-drop page reordering.
 *
 * Part of the AdminEditorModal.
 */

import React, { useState, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import {
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  GripVertical,
  RotateCcw,
} from 'lucide-react';
import { DraggableItem } from '../shared/DraggableItem';
import { useAdminEditorStore } from '../../store/adminEditorStore';
import { DEFAULT_SIDEBAR_PHASES, DEFAULT_PHASE_NAMES } from '../../constants/defaults';
import type { PhaseType, PhaseConfig, SidebarPage } from '../../types/adminEditor';
import { cn } from '@/shared/utils/cn';

export interface SidebarPhaseConfigTabProps {
  /** Additional CSS classes */
  className?: string;
}

/**
 * PhaseAccordion - Expandable section for a single phase
 */
interface PhaseAccordionProps {
  phase: PhaseConfig;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onPageReorder: (fromIndex: number, toIndex: number) => void;
  onPageVisibilityToggle: (pageId: string) => void;
}

const PhaseAccordion: React.FC<PhaseAccordionProps> = ({
  phase,
  isExpanded,
  onToggleExpand,
  onPageReorder,
  onPageVisibilityToggle,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  // Sort pages by order
  const sortedPages = useMemo(
    () => [...phase.pages].sort((a, b) => a.order - b.order),
    [phase.pages]
  );

  const activeItem = useMemo(
    () => sortedPages.find((page) => page.id === activeId),
    [sortedPages, activeId]
  );

  const visibleCount = phase.pages.filter((p) => p.visible).length;
  const totalCount = phase.pages.length;

  // Configure sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = sortedPages.findIndex((p) => p.id === active.id);
    const newIndex = sortedPages.findIndex((p) => p.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      onPageReorder(oldIndex, newIndex);
    }
  };

  // Get display name for phase
  const displayName = DEFAULT_PHASE_NAMES[phase.type] || phase.label;

  return (
    <div className="border border-dark-border/30 rounded-xl overflow-hidden">
      {/* Accordion Header */}
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between px-4 py-3 bg-dark-surface hover:bg-dark-surface/50 transition-all duration-150"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown size={18} className="text-dark-muted" />
          ) : (
            <ChevronRight size={18} className="text-dark-muted" />
          )}
          <span className="text-sm font-medium text-dark-text">{displayName}</span>
        </div>
        <span className="text-xs text-dark-muted">
          {visibleCount}/{totalCount} pages
        </span>
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div className="px-4 py-3 bg-dark-active border-t border-dark-border/30">
          {sortedPages.length === 0 ? (
            <p className="text-sm text-dark-muted italic text-center py-4">
              No pages in this phase
            </p>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortedPages.map((p) => p.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-1">
                  {sortedPages.map((page) => (
                    <DraggableItem
                      key={page.id}
                      id={page.id}
                      className={cn(
                        'px-2 py-2 bg-dark-surface border border-dark-border/30',
                        !page.visible && 'opacity-50'
                      )}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        {/* Page label */}
                        <div className="flex-1 min-w-0">
                          <span
                            className={cn(
                              'text-sm',
                              page.visible ? 'text-dark-text' : 'text-dark-muted'
                            )}
                          >
                            {page.label}
                          </span>
                          <p className="text-xs text-dark-muted truncate">
                            {page.route}
                          </p>
                        </div>

                        {/* Visibility toggle */}
                        <button
                          type="button"
                          onClick={() => onPageVisibilityToggle(page.id)}
                          className={cn(
                            'p-1.5 rounded transition-all duration-150 flex-shrink-0',
                            page.visible
                              ? 'text-green-400 hover:text-green-300 hover:bg-green-500/10'
                              : 'text-dark-muted hover:text-dark-text hover:bg-dark-surface/50'
                          )}
                          title={page.visible ? 'Hide page' : 'Show page'}
                        >
                          {page.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                        </button>
                      </div>
                    </DraggableItem>
                  ))}
                </div>
              </SortableContext>

              {/* Drag Overlay */}
              <DragOverlay>
                {activeItem && (
                  <div className="flex items-center gap-3 px-3 py-2 bg-gradient-to-br from-dark-surface/95 to-dark-bg/85 backdrop-blur-sm border border-brand-500 rounded shadow-lg">
                    <GripVertical size={16} className="text-dark-muted" />
                    <span className="text-sm text-dark-text">{activeItem.label}</span>
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * SidebarPhaseConfigTab - Configure sidebar phases
 *
 * Features:
 * - Accordion for each phase (Planning, Execution, Post-Campaign, Admin)
 * - Drag-drop reorder pages within each phase
 * - Toggle page visibility
 * - Show current pages per phase
 */
export const SidebarPhaseConfigTab: React.FC<SidebarPhaseConfigTabProps> = ({
  className = '',
}) => {
  // Track expanded phases
  const [expandedPhases, setExpandedPhases] = useState<Set<PhaseType>>(
    new Set(['planning'])
  );

  // Store state and actions
  const phases = useAdminEditorStore((state) => state.phases);
  const setPhases = useAdminEditorStore((state) => state.setPhases);
  const togglePageVisibility = useAdminEditorStore(
    (state) => state.togglePageVisibility
  );
  const reorderPage = useAdminEditorStore((state) => state.reorderPage);

  // Calculate total pages
  const totalPages = useMemo(
    () => phases.reduce((sum, phase) => sum + phase.pages.length, 0),
    [phases]
  );

  const visiblePages = useMemo(
    () =>
      phases.reduce(
        (sum, phase) => sum + phase.pages.filter((p) => p.visible).length,
        0
      ),
    [phases]
  );

  const togglePhaseExpanded = (phaseType: PhaseType) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(phaseType)) {
        next.delete(phaseType);
      } else {
        next.add(phaseType);
      }
      return next;
    });
  };

  const handlePageReorder = (phaseType: PhaseType, fromIndex: number, toIndex: number) => {
    const phase = phases.find((p) => p.type === phaseType);
    if (!phase) return;

    const sortedPages = [...phase.pages].sort((a, b) => a.order - b.order);
    const pageId = sortedPages[fromIndex]?.id;
    if (!pageId) return;

    reorderPage(pageId, phaseType, toIndex);
  };

  const handleResetToDefaults = () => {
    setPhases(
      DEFAULT_SIDEBAR_PHASES.map((phase) => ({
        ...phase,
        pages: [...phase.pages],
      }))
    );
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header with stats and reset button */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-dark-muted">
          <span className="text-green-400">{visiblePages} visible</span>
          <span className="ml-2 text-dark-muted">of {totalPages} pages</span>
        </div>
        <button
          type="button"
          onClick={handleResetToDefaults}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-surface/50 hover:bg-dark-active rounded transition-all duration-150 text-dark-muted hover:text-dark-text"
          title="Reset to default configuration"
        >
          <RotateCcw size={14} />
          Reset to Defaults
        </button>
      </div>

      {/* Instructions */}
      <p className="text-xs text-dark-muted">
        Expand each phase to reorder pages. Drag pages to change their order within a phase.
      </p>

      {/* Phase Accordions */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
        {phases.map((phase) => (
          <PhaseAccordion
            key={phase.type}
            phase={phase}
            isExpanded={expandedPhases.has(phase.type)}
            onToggleExpand={() => togglePhaseExpanded(phase.type)}
            onPageReorder={(from, to) => handlePageReorder(phase.type, from, to)}
            onPageVisibilityToggle={(pageId) =>
              togglePageVisibility(pageId, phase.type)
            }
          />
        ))}
      </div>

      {/* Footer info */}
      <div className="pt-2 border-t border-dark-border/30">
        <p className="text-xs text-dark-muted text-center">
          {phases.length} phases configured
        </p>
      </div>
    </div>
  );
};

export default SidebarPhaseConfigTab;
